import { memo, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";

import { useFindingStore, Finding } from "stores/FindingStore";
import { useIgnoreStore } from "stores/IgnoreStore";

import { notifyError, notifySuccess } from "utility/notify";

import SelectedFindings from "./SelectedFindings";

// -----------------------------------------------------------------------------

const BulkUnignoreDialog = memo(function BulkUnignoreDialog({
  findings,
  onClose,
}: BulkUnignoreDialogProps): JSX.Element {
  const [, ignoreActions] = useIgnoreStore();
  const [, findingActions] = useFindingStore();

  const [loading, setLoading] = useState(false);

  const onUnignore = () => {
    setLoading(true);

    const data = {
      uuids: findings.map((finding) => finding.uuid),
      onSuccess,
      onError,
    };

    ignoreActions.bulkUnignoreFinding(data);
  };

  const onSuccess = () => {
    findingActions.resetDeeplinkFindings();
    notifySuccess(`Unignored ${findings.length} findings`);
    setLoading(false);
    onClose();
  };

  const onError = (errorCount: number) => {
    if (errorCount === findings.length) {
      notifyError("Something went wrong unignoring the findings");
    } else {
      notifyError("Something went wrong unignoring some of the findings");
    }
    setLoading(false);
  };

  return (
    <Dialog open={true} onClose={onClose} fullWidth>
      <DialogTitle>Unignore Findings</DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          You are choosing to unignore {findings.length} selected findings:
        </Typography>
        <SelectedFindings findings={findings} />

        <Typography variant="body1">
          This will only impact these individual instances. Do you want to
          unignore them?
        </Typography>
      </DialogContent>

      <DialogActions sx={{ pt: 0, pb: 3, px: 3 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          loading={loading}
          variant="contained"
          onClick={onUnignore}
        >
          Unignore
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
});

export default BulkUnignoreDialog;

// -----------------------------------------------------------------------------

interface BulkUnignoreDialogProps {
  findings: Finding[];
  onClose: () => void;
}
